import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Modal, showToast, Text } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { Bundle } from "../../../../../../components/bundle/Bundle";
import { BundleDetails } from "../../../../../order/steps/components/BundleDetails";
import { BundleData } from "../../../../types";
import { PackageContext, SelectedBundlesType } from "../../../PackageContext";

function getCheckArgumentRecord(value: unknown): Record<string, unknown> {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
}

export function mergeBundleCheckArguments({
  bundleCheckArgsByType,
  userCheckArgsByType,
  checkType,
}: {
  bundleCheckArgsByType: Record<string, unknown>;
  userCheckArgsByType: Record<string, Record<string, unknown>>;
  checkType: string;
}): Record<string, unknown> {
  return {
    ...getCheckArgumentRecord(bundleCheckArgsByType[checkType]),
    ...getCheckArgumentRecord(userCheckArgsByType[checkType]),
  };
}

export function BundleSection() {
  const { t } = useTranslation();
  const { packageData, setPackage } = useContext(PackageContext);
  const [selectedBundle, setSelectedBundle] = useState<BundleData>();
  const [checkArgs, setCheckArgs] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const { data: bundles = [] }: { data?: BundleData[] } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/available-bundles/`
  );

  const onAdd = (bundle?: SelectedBundlesType) => {
    if (!bundle) {
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return;
    }

    // Keep server-provided bundle defaults (e.g. ordering_type) and merge user edits on top.
    const bundleCheckArgs = (bundle.check_types_with_arguments || {}) as Record<
      string,
      unknown
    >;
    const updatedBundle = {
      ...bundle,
      checks: bundle?.checks?.map((check) => ({
        ...check,
        check_arguments: mergeBundleCheckArguments({
          bundleCheckArgsByType: bundleCheckArgs,
          userCheckArgsByType: checkArgs,
          checkType: check.type,
        }),
      })),
    };

    setPackage({
      ...packageData,
      bundles: [...packageData.bundles, updatedBundle],
    });
    showToast(t("{{bundleName}} added", { bundleName: bundle.title }), {
      type: "success",
    });
    setSelectedBundle(undefined);
    setCheckArgs({});
  };

  const onRemove = (bundle?: SelectedBundlesType) => {
    if (!bundle) {
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return;
    }

    const newBundles = [
      ...packageData.bundles.filter(({ id }) => id !== bundle.id),
    ];
    setPackage({
      ...packageData,
      bundles: [...newBundles],
    });
    showToast(t("{{bundleName}} removed", { bundleName: bundle.title }), {
      type: "success",
    });
    setSelectedBundle(undefined);
    setCheckArgs({});
  };

  if (!bundles.length) {
    return <div />;
  }
  const handleUpdateArguments = (
    check_arguments: Record<string, unknown>,
    check: components["schemas"]["BundledDealCheckListChild"]
  ) => {
    setCheckArgs((prev) => ({ ...prev, [check.type]: check_arguments }));
  };

  return (
    <>
      <div className={styles.container}>
        <Text style="global/heading-large">{t("Bundled deals")}</Text>
        <div className={styles.bundles}>
          {bundles.map((bundle) => (
            <Bundle
              key={bundle.id}
              bundleName={bundle.title}
              checks={bundle.products.map((product) => product.name)}
              price={bundle.total ? bundle.total.toString() : ""}
              onPress={() => {
                setCheckArgs({});
                setSelectedBundle(bundle);
              }}
              isSelected={Boolean(
                packageData.bundles?.find(({ id }) => id === bundle?.id)
              )}
              testID={slugify(bundle.title)}
              currency={bundle.currency}
            />
          ))}
        </div>
      </div>
      <Modal
        isOpen={Boolean(selectedBundle)}
        className={styles.modalContainer}
        testID="bundle-details-modal"
      >
        <BundleDetails
          bundle={selectedBundle}
          isBundleApplied={
            !!packageData.bundles?.find(({ id }) => id === selectedBundle?.id)
          }
          onClose={() => {
            setCheckArgs({});
            setSelectedBundle(undefined);
          }}
          onAdd={onAdd}
          onRemove={onRemove}
          handleUpdateArguments={handleUpdateArguments}
        />
      </Modal>
    </>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  bundles: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
  }),
  modalContainer: css({
    width: "100%",
  }),
};
